import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Segment, Header, Button, Icon, Message, Progress, Card, Label, Divider } from 'semantic-ui-react';
import GlassPageWrapper from '../../../components/Layout/GlassPageWrapper';
import { quizAttemptService, QuizAttemptDto } from '../../../services/learning/QuizAttemptService';
import { quizService } from '../../../services/learning/QuizService';
import { useQuiz } from '../../../context/QuizContext';

const QuizResultPage: React.FC = () => {
  const { quizId } = useParams<{ quizId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { endQuiz } = useQuiz();
  const [attempt, setAttempt] = useState<QuizAttemptDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadResult = async () => {
      if (!quizId) {
        setError('ID do questionário não fornecido');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Tentar obter da navegação primeiro
        const stateAttempt = location.state?.attempt as QuizAttemptDto | undefined;
        
        if (stateAttempt) {
          setAttempt(stateAttempt);
          // Finalizar quiz no contexto quando resultado for carregado
          endQuiz();
        } else {
          // Se não tiver no state, buscar do histórico (última tentativa completada)
          // Por enquanto, vamos precisar do attemptId - isso precisaria ser ajustado
          setError('Tentativa não encontrada. Por favor, acesse através da página de questionários.');
          // Garantir que o quiz seja finalizado mesmo em caso de erro
          endQuiz();
        }
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar resultado');
      } finally {
        setLoading(false);
      }
    };

    loadResult();
  }, [quizId, location.state]);

  // Esconder texto interno da barra de progresso
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .ui.progress .bar > .progress {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const formatTime = (seconds?: number): string => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <GlassPageWrapper title="Carregando Resultado" icon="spinner">
        <Message info>
          <Icon name="spinner" loading />
          Carregando resultado do questionário...
        </Message>
      </GlassPageWrapper>
    );
  }

  if (error || !attempt) {
    return (
      <GlassPageWrapper title="Erro" icon="exclamation triangle">
        <Message negative>
          <Message.Header>Erro</Message.Header>
          <p>{error || 'Resultado não encontrado'}</p>
          <Button onClick={() => navigate('/learning')}>Voltar</Button>
        </Message>
      </GlassPageWrapper>
    );
  }

  const score = attempt.score ?? 0;
  const isPassed = attempt.isPassed ?? false;
  const minimumScore = 70; // Isso deveria vir do quiz, mas por enquanto usamos um valor padrão

  const handleReviewClick = () => {
    if (!quizId || !attempt) return;
    navigate(`/learning/quizzes/${quizId}/review/${attempt.id}`);
  };

  return (
    <GlassPageWrapper title="Resultado do Questionário" icon="clipboard check">
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '0 20px'
      }}>
        <Card fluid>
          <Card.Content>
            <Header as="h2" textAlign="center" style={{ marginBottom: '20px', fontSize: '1.5em' }}>
              <Icon name={isPassed ? 'check circle' : 'times circle'} 
                    color={isPassed ? 'green' : 'red'} 
                    size="large" />
              <Header.Content>
                {isPassed ? 'Aprovado!' : 'Reprovado'}
              </Header.Content>
            </Header>

            <Segment style={{ padding: '24px', backgroundColor: '#f8f9fa', marginBottom: '20px' }}>
              <Header as="h3" style={{ fontSize: '1.1em', marginBottom: '16px' }}>
                <Icon name="chart bar" />
                Progresso
              </Header>
              <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                <div style={{
                  fontSize: '2em',
                  fontWeight: 'bold',
                  color: '#db2828'
                }}>
                  {score.toFixed(1)}%
                </div>
              </div>
              <Progress
                percent={score}
                progress
                color={isPassed ? 'green' : 'red'}
                size="medium"
                style={{ margin: 0 }}
              />
              <div style={{ textAlign: 'center', color: '#666', fontSize: '0.9em', marginTop: '12px' }}>
                Pontuação mínima para aprovação: {minimumScore}%
              </div>
            </Segment>

            <Divider style={{ margin: '20px 0' }} />

            <Segment style={{ padding: '20px' }}>
              <Header as="h3" style={{ fontSize: '1.1em', marginBottom: '16px' }}>
                <Icon name="info circle" />
                Informações da Tentativa
              </Header>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginTop: '16px' }}>
                <div>
                  <Label color="blue" size="medium" style={{ width: '100%', textAlign: 'center' }}>
                    <Icon name="clock" />
                    Tempo Gasto
                  </Label>
                  <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '1.1em', fontWeight: 'bold' }}>
                    {formatTime(attempt.timeSpentSeconds)}
                  </div>
                </div>
                
                <div>
                  <Label color="purple" size="medium" style={{ width: '100%', textAlign: 'center' }}>
                    <Icon name="calendar" />
                    Data de Conclusão
                  </Label>
                  <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '1.1em', fontWeight: 'bold' }}>
                    {attempt.completedAt 
                      ? new Date(attempt.completedAt).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      : 'N/A'}
                  </div>
                </div>

                <div>
                  <Label color={isPassed ? 'green' : 'red'} size="medium" style={{ width: '100%', textAlign: 'center' }}>
                    <Icon name={isPassed ? 'check' : 'times'} />
                    Status
                  </Label>
                  <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '1.1em', fontWeight: 'bold' }}>
                    {isPassed ? 'Aprovado' : 'Reprovado'}
                  </div>
                </div>
              </div>
            </Segment>

            <Divider style={{ margin: '20px 0' }} />

            <Message info style={{ padding: '16px' }}>
              <Message.Header style={{ fontSize: '1em' }}>
                <Icon name="info circle" />
                Informação
              </Message.Header>
              <p style={{ marginTop: '8px', fontSize: '0.95em' }}>
                {isPassed 
                  ? 'Parabéns! Você foi aprovado neste questionário. Continue estudando para manter seu conhecimento atualizado.'
                  : `Você não atingiu a pontuação mínima de ${minimumScore}% necessária para aprovação. Revise o material e tente novamente quando estiver pronto.`}
              </p>
            </Message>

            <div style={{ textAlign: 'center', marginTop: '24px', display: 'flex', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <Button
                primary
                size="medium"
                icon="arrow left"
                content="Voltar para Questionários"
                onClick={() => navigate('/learning')}
              />
              {quizId && attempt && (
                <Button
                  basic
                  color="blue"
                  size="medium"
                  icon
                  labelPosition="left"
                  onClick={handleReviewClick}
                >
                  <Icon name="clipboard list" />
                  Revisar suas respostas
                </Button>
              )}
            </div>
          </Card.Content>
        </Card>
      </div>
    </GlassPageWrapper>
  );
};

export default QuizResultPage;
